/**
 * Shop Tracker - Consent Banner v1.1.0
 * --------------------------------------
 * EU-compliant cookiebanner volgens EDPB Guidelines 05/2020 en AP-richtlijnen.
 *
 * Compliance:
 *  - Reject net zo prominent als Accept (geen dark patterns)
 *  - Geen pre-ticked boxes voor non-noodzakelijke categorieen
 *  - Per categorie een uitleg/description (informatieplicht)
 *  - Persistente withdrawal-toegang via floating button
 *  - Consent verloopt na 12 maanden -> opnieuw vragen
 *  - Toetsenbord-toegankelijk (Tab focus trap, Esc = sluiten = geen consent)
 *  - Banner kan niet "weg geklikt" worden zonder keuze
 *  - Layered banner: kort + uitklapbare details
 *  - Link naar volledig privacy-statement
 *
 * Public API: window.ShopBanner.{ ready, getState, getTerms,
 *                                  openSettings, withdraw, reset }
 */
(function () {
  'use strict';

  // ===== 0a. Self-inject banner.css als die nog niet via plugin/HTML is
  // geladen. Vereist voor GTM-template-route (sandbox heeft geen
  // injectStylesheet-API). Idempotent: skip als CSS al aanwezig is.
  (function ensureBannerCss() {
    const existing = document.querySelectorAll('link[rel="stylesheet"]');
    for (let i = 0; i < existing.length; i++) {
      // Match `banner.css` aan einde van pad (eventueel met `gmu-`/`-`/`_`-prefix)
      // gevolgd door einde, `?query` of `#hash`. Dekt zowel consent-api conventie
      // (`/banner.css`) als WP/Magento/Shopify (`gmu-banner.css`).
      if (existing[i].href && /banner\.css(\?|#|$)/.test(existing[i].href)) return;
    }
    let src = null;
    if (document.currentScript && document.currentScript.src) {
      src = document.currentScript.src;
    } else {
      const scripts = document.getElementsByTagName('script');
      for (let j = scripts.length - 1; j >= 0; j--) {
        if (scripts[j].src && scripts[j].src.indexOf('banner.js') !== -1) {
          src = scripts[j].src;
          break;
        }
      }
    }
    if (!src) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = src.replace(/banner\.js(\?.*)?$/, 'banner.css');
    link.onerror = function () {
      if (window.console) console.warn('[ShopBanner] banner.css load failed: ' + link.href);
    };
    document.head.appendChild(link);
  })();

  // ===== 0. Helpers + i18n (gedeclareerd vroeg zodat unit tests via
  // window.__ShopBannerInternal kunnen testen zonder dat init() draait) =====
  let LANG = 'nl-NL';

  // Normalize naar BCP-47-vorm met region. Shopify levert `localization.language.iso_code`
  // als korte tag (`de`, `en`, `fr`); banner UI_STRINGS gebruikt volledige tags (`de-DE`, `en-GB`).
  // Mapping primary→default-region houden we beperkt tot wat UI_STRINGS ondersteunt; verkrijgt
  // ook `de_DE`-style (Shopify-fallback met underscore) en hoofdletter-varianten.
  function normalizeLang(raw) {
    if (!raw) return null;
    const parts = String(raw).replace('_', '-').split('-');
    const primary = parts[0].toLowerCase();
    if (parts.length >= 2) return `${primary}-${parts[1].toUpperCase()}`;
    const defaults = { nl: 'nl-NL', en: 'en-GB', de: 'de-DE', fr: 'fr-FR' };
    return defaults[primary] || primary;
  }

  function escHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, c => (
      {'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]
    ));
  }

  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }

  const ctx = window.SHOP_CONTEXT;
  if (!ctx || !ctx.consent) {
    console.warn('[ShopBanner] missing SHOP_CONTEXT.consent - banner inactive');
    // Expose helpers ook in deze inactive-modus zodat tests blijven werken
    if (typeof window !== 'undefined') {
      window.__ShopBannerInternal = window.__ShopBannerInternal || {};
    }
    return;
  }
  const cfg = ctx.consent;

  // ===== 1. Consent default - kritiek: voor alle andere scripts =====
  // Region-scoped per Google best practice: 'denied' alléén in de regio's waar
  // we een banner tonen (EER/VK/CH); 'granted' daarbuiten → geen meetverlies.
  // De region-codes MOETEN ISO 3166-2 zijn — 'EU'/'EEA' zijn ONgeldig en worden
  // door Consent Mode genegeerd. Een region-loze default dekt alle overige regio's.
  // Zie https://developers.google.com/tag-platform/security/guides/consent
  const GDPR_REGIONS = [
    'AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU','IE','IT',
    'LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES','SE',   // EU-27
    'IS','LI','NO',                                                // EER (niet-EU)
    'GB','CH'                                                      // VK + Zwitserland
  ];

  // 1a) EER/VK/CH → denied tot de banner-update toestemming geeft
  gtag('consent', 'default', {
    ad_storage:            'denied',
    ad_user_data:          'denied',
    ad_personalization:    'denied',
    analytics_storage:     'denied',
    functionality_storage: 'granted',
    security_storage:      'granted',
    wait_for_update:       500,
    region:                GDPR_REGIONS
  });

  // 1b) Alle overige regio's (buiten EER) → granted = volledige meting
  gtag('consent', 'default', {
    ad_storage:            'granted',
    ad_user_data:          'granted',
    ad_personalization:    'granted',
    analytics_storage:     'granted',
    functionality_storage: 'granted',
    security_storage:      'granted'
  });

  // ===== 2. Constants =====
  const VERSION         = '1.3.0';
  // Page-taal: SHOP_CONTEXT.shop.language (door plugin gerenderd) of HTML lang attribuut.
  // Genormaliseerd naar BCP-47-vorm met region (`de` → `de-DE`) zodat UI_STRINGS-lookup
  // en API-response (na fuzzy-match) consistent zijn.
  LANG = normalizeLang(ctx.shop?.language || document.documentElement.lang) || 'nl-NL';
  // Cache-key bevat taal én banner-versie; oude cache wordt zo automatisch
  // gedumpt bij een banner-upgrade die het cache-formaat wijzigt.
  const CACHE_KEY       = `_consent_terms_v${VERSION}_${cfg.klant_id}_${LANG}`;
  const STATE_KEY       = `_consent_state_${cfg.klant_id}`;  // consent-state taal-onafhankelijk
  const TERMS_TTL_MS    = 6 * 60 * 60 * 1000;            // 6u terms-cache
  const CONSENT_TTL_MS  = 365 * 24 * 60 * 60 * 1000;     // 12 maanden consent (AP richtlijn)

  // ===== 3. Storage helpers =====
  const ls = {
    get: (k) => { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : null; } catch { return null; } },
    set: (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} },
    del: (k) => { try { localStorage.removeItem(k); } catch {} }
  };

  // ===== 4. API helpers =====
  async function api(method, path, body) {
    const opts = { method, headers: { 'X-API-Key': cfg.public_key } };
    if (body) {
      opts.headers['Content-Type'] = 'application/json';
      opts.body = JSON.stringify(body);
    }
    const r = await fetch(cfg.api_url + path, opts);
    if (!r.ok) {
      const txt = await r.text().catch(() => '');
      throw new Error(`API ${method} ${path} -> ${r.status} ${txt}`);
    }
    return r.status === 204 ? null : r.json();
  }

  // ===== 5. Load terms with cache + hash check (per language) =====
  async function loadTerms() {
    const langQuery = `?language=${encodeURIComponent(LANG)}`;
    const cached = ls.get(CACHE_KEY);
    if (cached && cached.expires > Date.now()) {
      try {
        const h = await api('GET', `/klant/${cfg.klant_id}/terms/hash${langQuery}`);
        const termsOk = h.md5_hash === cached.md5_hash;
        const catsOk  = (h.categories_md5_hash || null) === (cached.categories_md5_hash || null);
        if (termsOk && catsOk) {
          console.debug('[ShopBanner] cache hit, both hashes matched (lang=' + LANG + ')');
          return cached;
        }
        console.debug('[ShopBanner] hash changed, refetching (terms=' + termsOk + ' cats=' + catsOk + ')');
      } catch (e) {
        console.warn('[ShopBanner] hash check failed, using stale cache', e.message);
        return cached;
      }
    }
    let fresh = await api('GET', `/klant/${cfg.klant_id}/terms${langQuery}`);
    // Defensieve fallback: als de server (om welke reden dan ook) géén terms
    // teruggeeft voor deze taal, probeer nl-NL als laatste redmiddel.
    if (!fresh.current_terms && LANG !== 'nl-NL') {
      console.warn('[ShopBanner] no terms for ' + LANG + ', retrying with nl-NL');
      fresh = await api('GET', `/klant/${cfg.klant_id}/terms?language=nl-NL`);
    }
    if (!fresh.current_terms) {
      throw new Error('No published terms for klant ' + cfg.klant_id + ' / language ' + LANG);
    }
    // Sync LANG met daadwerkelijke response-language (server-side fuzzy match
    // kan `de` resolven naar `de-DE`). Zorgt dat UI_STRINGS-lookup matched.
    if (fresh.current_terms.language && UI_STRINGS[fresh.current_terms.language]) {
      LANG = fresh.current_terms.language;
    }
    const entry = {
      ...fresh.current_terms,
      categories:          fresh.categories,
      categories_md5_hash: fresh.categories_md5_hash || null,
      expires:             Date.now() + TERMS_TTL_MS
    };
    ls.set(CACHE_KEY, entry);
    return entry;
  }

  // ===== 6. Map klant categories -> Google Consent Mode v2 storage =====
  function mapToStorage(choices) {
    const out = { security_storage: 'granted', functionality_storage: 'granted' };
    const g = (k) => choices[k] === true ? 'granted' : 'denied';
    if ('analytics' in choices) out.analytics_storage = g('analytics');
    if ('marketing' in choices) {
      const v = g('marketing');
      out.ad_storage = v;
      out.ad_user_data = v;
      out.ad_personalization = v;
    }
    if ('social' in choices && choices.social) out.ad_storage = 'granted';
    if ('personalization' in choices) out.personalization_storage = g('personalization');
    return out;
  }

  // ===== 6b. i18n: vaste UI-strings per taal =====
  // Vertaalt knop-labels, footer-tekst en fallback-banner; klant-specifieke
  // teksten (intro, categorie-descriptions) komen uit de API.
  //
  // SECURITY: deze tabel is een compile-time constant en sommige strings
  // (persistent_meta, fallback_text, fallback_meta) bevatten <strong>/<a>
  // tags die via innerHTML in de DOM komen — daarom NOOIT load-from-API of
  // user-input hier doorheen. Vars die via T(key, vars) worden gesubstitueerd
  // worden door T() auto-geescaped voor extra zekerheid.
  const UI_STRINGS = {
    'nl-NL': {
      title:           'Wij gebruiken cookies',
      policy_link:     'Lees ons volledige privacy-statement',
      settings_label:  'Voorkeuren aanpassen per categorie',
      save_btn:        'Mijn voorkeuren opslaan',
      reject_btn:      'Alleen noodzakelijk',
      accept_btn:      'Alles accepteren',
      always_active:   'altijd actief',
      default_on:      'standaard aan',
      version_prefix:  'Versie',
      persistent_meta: 'Je keuze geldt 12 maanden. Wil je deze later wijzigen of intrekken? Klik op de cookie-knop linksonder in beeld.',
      fallback_title:  'Cookies',
      fallback_text:   'Deze website gebruikt cookies. Op dit moment kunnen we je voorkeuren niet opslaan, dus alleen <strong>strikt noodzakelijke</strong> cookies zijn actief. Analytics en marketing zijn uitgeschakeld.',
      fallback_meta:   'Cookiebanner tijdelijk niet beschikbaar (ref: {reason}). <a href="#" data-action="retry">Pagina herladen</a> om opnieuw te proberen.',
      close_btn:       'Sluiten',
      fab_label:       'Cookie-instellingen',
      fab_aria:        'Cookie-instellingen openen',
      fab_title:       'Klik om je cookie-voorkeuren te wijzigen',
      save_error:      'Er ging iets mis bij het opslaan. Probeer opnieuw.',
      details_link:    'Details tonen',
      details_hide:    'Details verbergen',
      always_required: 'altijd',
      compact_title:   'Cookie-instellingen',
      withdraw_btn:    'Instemming intrekken',
      change_btn:      'Voorkeuren bijwerken',
    },
    'en-GB': {
      title:           'We use cookies',
      policy_link:     'Read our full privacy statement',
      settings_label:  'Adjust preferences per category',
      save_btn:        'Save my preferences',
      reject_btn:      'Only necessary',
      accept_btn:      'Accept all',
      always_active:   'always on',
      default_on:      'default on',
      version_prefix:  'Version',
      persistent_meta: 'Your choice is valid for 12 months. Want to change or withdraw it later? Click the cookie button at the bottom left.',
      fallback_title:  'Cookies',
      fallback_text:   'This website uses cookies. We can’t save your preferences right now, so only <strong>strictly necessary</strong> cookies are active. Analytics and marketing are switched off.',
      fallback_meta:   'Cookie banner temporarily unavailable (ref: {reason}). <a href="#" data-action="retry">Reload the page</a> to try again.',
      close_btn:       'Close',
      fab_label:       'Cookie settings',
      fab_aria:        'Open cookie settings',
      fab_title:       'Click to change your cookie preferences',
      save_error:      'Something went wrong while saving. Please try again.',
      details_link:    'Show details',
      details_hide:    'Hide details',
      always_required: 'required',
      compact_title:   'Cookie settings',
      withdraw_btn:    'Withdraw consent',
      change_btn:      'Update preferences',
    },
    'de-DE': {
      title:           'Wir verwenden Cookies',
      policy_link:     'Lesen Sie unsere vollständige Datenschutzerklärung',
      settings_label:  'Einstellungen pro Kategorie anpassen',
      save_btn:        'Meine Einstellungen speichern',
      reject_btn:      'Nur notwendige',
      accept_btn:      'Alle akzeptieren',
      always_active:   'immer aktiv',
      default_on:      'standardmäßig aktiv',
      version_prefix:  'Version',
      persistent_meta: 'Ihre Auswahl ist 12 Monate gültig. Später ändern oder widerrufen? Klicken Sie auf die Cookie-Schaltfläche unten links.',
      fallback_title:  'Cookies',
      fallback_text:   'Diese Website verwendet Cookies. Im Moment können wir Ihre Einstellungen nicht speichern, sodass nur <strong>unbedingt erforderliche</strong> Cookies aktiv sind. Analyse und Marketing sind deaktiviert.',
      fallback_meta:   'Cookie-Banner vorübergehend nicht verfügbar (Ref: {reason}). <a href="#" data-action="retry">Seite neu laden</a> um es erneut zu versuchen.',
      close_btn:       'Schließen',
      fab_label:       'Cookie-Einstellungen',
      fab_aria:        'Cookie-Einstellungen öffnen',
      fab_title:       'Klicken Sie hier, um Ihre Cookie-Einstellungen zu ändern',
      save_error:      'Beim Speichern ist etwas schiefgelaufen. Bitte erneut versuchen.',
      details_link:    'Details anzeigen',
      details_hide:    'Details ausblenden',
      always_required: 'erforderlich',
      compact_title:   'Cookie-Einstellungen',
      withdraw_btn:    'Einwilligung widerrufen',
      change_btn:      'Einstellungen aktualisieren',
    },
    'fr-FR': {
      title:           'Nous utilisons des cookies',
      policy_link:     'Lire notre déclaration de confidentialité complète',
      settings_label:  'Modifier les préférences par catégorie',
      save_btn:        'Enregistrer mes préférences',
      reject_btn:      'Uniquement nécessaires',
      accept_btn:      'Tout accepter',
      always_active:   'toujours actif',
      default_on:      'activé par défaut',
      version_prefix:  'Version',
      persistent_meta: 'Votre choix est valable 12 mois. Vous souhaitez le modifier ou le retirer plus tard ? Cliquez sur le bouton cookie en bas à gauche.',
      fallback_title:  'Cookies',
      fallback_text:   'Ce site utilise des cookies. Pour le moment, nous ne pouvons pas enregistrer vos préférences ; seuls les cookies <strong>strictement nécessaires</strong> sont actifs. L’analyse et le marketing sont désactivés.',
      fallback_meta:   'Bannière de cookies temporairement indisponible (réf. : {reason}). <a href="#" data-action="retry">Recharger la page</a> pour réessayer.',
      close_btn:       'Fermer',
      fab_label:       'Paramètres des cookies',
      fab_aria:        'Ouvrir les paramètres des cookies',
      fab_title:       'Cliquez pour modifier vos préférences de cookies',
      save_error:      'Une erreur est survenue lors de l’enregistrement. Veuillez réessayer.',
      details_link:    'Afficher les détails',
      details_hide:    'Masquer les détails',
      always_required: 'requis',
      compact_title:   'Paramètres des cookies',
      withdraw_btn:    'Retirer le consentement',
      change_btn:      'Mettre à jour les préférences',
    },
  };
  function T(key, vars) {
    const tr = UI_STRINGS[LANG] || UI_STRINGS['nl-NL'];
    let base = tr[key] || UI_STRINGS['nl-NL'][key] || '';
    // Vars worden HTML-geescaped omdat sommige UI_STRINGS via innerHTML
    // worden gerenderd (zie SECURITY-comment boven UI_STRINGS). De template
    // zelf is trusted, maar de vars zijn runtime-waarden (bv. reason-codes)
    // die we niet als raw HTML willen toelaten.
    if (vars) for (const k in vars) {
      const safe = escHtml(vars[k] == null ? '' : String(vars[k]));
      base = base.replace('{' + k + '}', safe);
    }
    return base;
  }

  // Test-only hook: exposeert pure helpers (geen state, geen API-keys) zodat
  // Vitest tegen UI_STRINGS-completeness + T()/escHtml-gedrag kan asserten.
  // In productie heeft de banner zelf hier geen functionele afhankelijkheid
  // van; verwijder deze regel als je de surface minimaal wil houden.
  if (typeof window !== 'undefined') {
    window.__ShopBannerInternal = {
      T, UI_STRINGS, escHtml, normalizeLang,
      _setLang: (l) => { LANG = l; },
      _getLang: () => LANG,
    };
  }

  // ===== 7. Banner UI =====
  let _bannerEl = null;
  let _floatingBtn = null;
  let _previousFocus = null;

  function trapFocus(container) {
    const focusables = container.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"]), a[href]'
    );
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last  = focusables[focusables.length - 1];
    container.addEventListener('keydown', (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault(); last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault(); first.focus();
        }
      }
    });
  }

  /**
   * Fail-safe banner: API onbereikbaar, klant_id onbekend, key revoked, geen
   * gepubliceerde terms — alle gevallen waarin we GEEN consent kunnen
   * registreren. Toont uitleg + sluit-actie, GEEN Accept/Reject knoppen.
   * Consent Mode blijft op `denied` (default-state in stap 1).
   */
  function renderFallbackBanner(reason) {
    if (_bannerEl) { _bannerEl.remove(); _bannerEl = null; }
    _previousFocus = document.activeElement;

    const el = document.createElement('div');
    el.className = 'shopbanner shopbanner--fallback';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-labelledby', 'shopbanner-title');
    el.setAttribute('aria-describedby', 'shopbanner-text');

    el.innerHTML = `
      <div class="shopbanner__panel" role="document">
        <div class="shopbanner__body">
          <h2 id="shopbanner-title" class="shopbanner__title">${escHtml(T('fallback_title'))}</h2>
          <p id="shopbanner-text" class="shopbanner__text">${T('fallback_text')}</p>
          <p class="shopbanner__meta">${T('fallback_meta', { reason: reason || 'unknown' })}</p>
        </div>
        <div class="shopbanner__actions">
          <button type="button" class="shopbanner__btn shopbanner__btn--outline" data-action="dismiss">${escHtml(T('close_btn'))}</button>
        </div>
        ${buildBrandHtml()}
      </div>
    `;
    document.body.appendChild(el);
    _bannerEl = el;
    trapFocus(el);

    setTimeout(() => el.querySelector('#shopbanner-title')?.focus({ preventScroll: true }), 50);

    el.addEventListener('click', (e) => {
      const target = e.target.closest('[data-action]');
      if (!target) return;
      e.preventDefault();
      if (target.dataset.action === 'retry') {
        location.reload();
      } else if (target.dataset.action === 'dismiss') {
        hideBanner();
      }
    });

    el.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        hideBanner();
      }
    });

    window.dataLayer.push({
      event: 'cmp_fallback_shown',
      reason: reason || 'unknown',
    });
  }

  /**
   * Basis-URL waar banner.js + assets vandaan worden geserveerd. Afgeleid van
   * het script-tag dat banner.js heeft geladen, zodat we automatisch de juiste
   * environment volgen (lokaal vs. productie) zonder extra config-veld.
   * Fallback: SHOP_CONTEXT.consent.banner_asset_url (indien expliciet gezet).
   *
   * Naming-conventies per platform:
   *   - self-hosted (consent-api):  /banner/v1/banner.js
   *   - WooCommerce / Shopify:      /assets/.../gmu-banner.js
   *   - Magento:                    /static/.../banner.js
   * Banner.js voegt zelf "/assets/${filename}" toe aan deze base, dus
   * een eventueel reeds aanwezig "/assets"-suffix wordt afgestript om
   * dubbele paden (zoals "/cdn/shop/t/6/assets/assets/...") te vermijden.
   */
  function bannerBaseUrl() {
    try {
      const script = document.querySelector('script[src*="banner.js"]')
                  || document.currentScript;
      if (script?.src) {
        return script.src
          .replace(/\/(?:gmu-)?banner\.js(\?.*)?$/, '')
          .replace(/\/assets\/?$/, '');
      }
    } catch { /* ignore */ }
    return (cfg.banner_asset_url || '')
      .replace(/\/+$/, '')
      .replace(/\/assets\/?$/, '');
  }

  /**
   * URL naar het cookie-FAB-SVG (cookie-shape met G-icon + GMU + tagline,
   * `gmu-cookie-fab-full.svg`) — gebruikt als icoon voor de "cookie-
   * instellingen opnieuw openen"-knop nadat de gebruiker een keuze heeft
   * gemaakt.
   *
   * Platform-specifieke override via SHOP_CONTEXT.consent.logo_url_cookie_fab
   * wint — nodig voor Shopify waar /assets/ flat is (geen sub-folder) en de
   * URL volledig door Liquid wordt gerendered. Magento gebruikt de default
   * {base}/assets/-conventie via getViewFileUrl.
   */
  function cookieFabIconUrl() {
    return cfg.logo_url_cookie_fab || `${bannerBaseUrl()}/assets/gmu-cookie-fab-full.svg`;
  }

  /**
   * Bouwt de toggles-rij (inline of compact). `display` bepaalt CSS-shape;
   * "always required"-toggles zijn disabled en visueel gemarkeerd.
   */
  function buildCategoriesHtml(visibleCategories, currentChoices) {
    return visibleCategories.map(c => {
      const code     = escHtml(c.code);
      const label    = escHtml(c.label);
      const required = !!c.required;
      const stored   = currentChoices ? !!currentChoices[c.code] : null;
      // `default_enabled` alleen toepassen bij eerste keer (geen stored state).
      // Bij re-open: respecteer eerdere keuze.
      const checked  = stored !== null ? stored : (required || !!c.default_enabled);
      return `
        <div class="shopbanner__cat">
          <label class="shopbanner__toggle" title="${label}">
            <input type="checkbox" data-code="${code}"
              ${checked ? 'checked' : ''}
              ${required ? 'disabled aria-disabled="true"' : ''}>
            <span class="shopbanner__toggle-slider" aria-hidden="true"></span>
          </label>
          <span class="shopbanner__cat-label">${label}${required ? ` <span class="shopbanner__cat-required">(${escHtml(T('always_required'))})</span>` : ''}</span>
        </div>
      `;
    }).join('');
  }

  /**
   * Per-categorie descriptions als <dl>. Wordt pas getoond als
   * "Details tonen"-link is geklikt.
   */
  function buildCategoryDetailsHtml(visibleCategories) {
    return `<dl>${visibleCategories.map(c => `
      <dt>${escHtml(c.label)}</dt>
      <dd>${escHtml(c.description || _defaultDesc(c.code))}</dd>
    `).join('')}</dl>`;
  }

  /**
   * Taal-specifieke gmu.online-link: elke taal krijgt zijn eigen /{xx}/-pad.
   * BCP-47 tag (nl-NL, en-US, de-DE, ...) wordt afgekapt naar 2-letter
   * primary subtag. Onbekende/lege LANG valt terug op /nl/.
   */
  function gmuMarketingUrl() {
    const lang2 = (LANG || 'nl-NL').slice(0, 2).toLowerCase();
    return `https://gmu.online/${lang2}/`;
  }

  function buildBrandHtml() {
    const href = gmuMarketingUrl();
    return `<a href="${escHtml(href)}" target="_blank" rel="noopener" class="shopbanner__brand">
      <span class="shopbanner__brand-text">Marketing by GMU</span>
    </a>`;
  }

  /**
   * Render de eerste-laad full-width footer-bar.
   * GDPR-vorm: geen overlay, toggles inline, 3 even-prominent buttons.
   */
  function renderBanner(terms, categories, onChoice) {
    if (_bannerEl) { _bannerEl.remove(); _bannerEl = null; }
    _previousFocus = document.activeElement;

    const el = document.createElement('div');
    el.className = 'shopbanner';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-labelledby', 'shopbanner-title');
    el.setAttribute('aria-describedby', 'shopbanner-text');
    // Geen aria-modal: bezoeker mag pagina blijven gebruiken (anti cookie-wall).

    const mainCat           = categories.find(c => c.code === 'main');
    const visibleCategories = categories.filter(c => c.code !== 'main');
    const title  = (mainCat && mainCat.label)       || T('title');
    const intro  = (mainCat && mainCat.description) || '';
    // Privacy-URL fallback-chain (eerste match wint):
    //   1. SHOP_CONTEXT.consent.privacy_url_override (theme-level, bv. Shopify
    //      met lokale /policies/privacy-policy of een eigen /pages/-route)
    //   2. terms.content.policy_url (centraal in GMU Beheer-paneel beheerd)
    //   3. niets → link verschijnt niet
    const policy = cfg.privacy_url_override || terms.content?.policy_url;

    el.innerHTML = `
      <div class="shopbanner__panel" role="document">
        <div class="shopbanner__body">
          <h2 id="shopbanner-title" class="shopbanner__title">${escHtml(title)}</h2>
          <p id="shopbanner-text" class="shopbanner__text">${escHtml(intro)}</p>
          <p class="shopbanner__policy">
            ${policy ? `<a href="${escHtml(policy)}" target="_blank" rel="noopener">${escHtml(T('policy_link'))}</a>` : ''}
            <button type="button" class="shopbanner__details-link" data-action="toggle-details" aria-expanded="false">${escHtml(T('details_link'))}</button>
          </p>
        </div>
        <div class="shopbanner__actions">
          <button type="button" class="shopbanner__btn shopbanner__btn--primary" data-action="accept">${escHtml(T('accept_btn'))}</button>
          <button type="button" class="shopbanner__btn shopbanner__btn--save"    data-action="save">${escHtml(T('save_btn'))}</button>
          <button type="button" class="shopbanner__btn shopbanner__btn--outline" data-action="reject">${escHtml(T('reject_btn'))}</button>
        </div>
        <div class="shopbanner__categories" role="group" aria-label="${escHtml(T('settings_label'))}">
          ${buildCategoriesHtml(visibleCategories, null)}
        </div>
        <div class="shopbanner__cat-details" data-open="false" aria-live="polite">
          ${buildCategoryDetailsHtml(visibleCategories)}
        </div>
        ${buildBrandHtml()}
      </div>
    `;
    document.body.appendChild(el);
    _bannerEl = el;
    trapFocus(el);

    setTimeout(() => el.querySelector('#shopbanner-title')?.focus({ preventScroll: true }), 50);

    el.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        const choices = {};
        categories.forEach(c => choices[c.code] = !!c.required);
        onChoice(choices, 'reject_all');
      }
    });

    el.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-action]');
      if (!btn) return;
      const action = btn.dataset.action;

      if (action === 'toggle-details') {
        const block = el.querySelector('.shopbanner__cat-details');
        const open  = block.dataset.open === 'true';
        block.dataset.open = open ? 'false' : 'true';
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        btn.textContent = open ? T('details_link') : T('details_hide');
        return;
      }

      const choices = {};
      categories.forEach(c => choices[c.code] = !!c.required);

      if (action === 'accept') {
        categories.forEach(c => choices[c.code] = true);
        onChoice(choices, 'accept_all');
      } else if (action === 'reject') {
        onChoice(choices, 'reject_all');
      } else if (action === 'save') {
        el.querySelectorAll('input[data-code]').forEach(inp => {
          choices[inp.dataset.code] = inp.checked;
        });
        onChoice(choices, 'custom');
      }
    });
  }

  /**
   * Render een compact panel bottom-left (re-open na keuze). Toont huidige
   * toggle-state + 2 buttons (Intrekken / Bijwerken). Tweede-laag UI, niet
   * de full-width bar.
   */
  function renderCompactPanel(terms, categories, currentChoices, onWithdraw, onUpdate) {
    if (_bannerEl) { _bannerEl.remove(); _bannerEl = null; }
    _previousFocus = document.activeElement;

    const el = document.createElement('div');
    el.className = 'shopbanner shopbanner--compact';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-labelledby', 'shopbanner-title');

    const visibleCategories = categories.filter(c => c.code !== 'main');

    el.innerHTML = `
      <div class="shopbanner__panel" role="document">
        <button type="button" class="shopbanner__close" data-action="close" aria-label="${escHtml(T('close_btn'))}">&times;</button>
        <div class="shopbanner__body">
          <h2 id="shopbanner-title" class="shopbanner__title" tabindex="-1">${escHtml(T('compact_title'))}</h2>
        </div>
        <div class="shopbanner__categories" role="group" aria-label="${escHtml(T('settings_label'))}">
          ${buildCategoriesHtml(visibleCategories, currentChoices)}
        </div>
        <div class="shopbanner__actions">
          <button type="button" class="shopbanner__btn shopbanner__btn--outline" data-action="withdraw">${escHtml(T('withdraw_btn'))}</button>
          <button type="button" class="shopbanner__btn shopbanner__btn--primary" data-action="update">${escHtml(T('change_btn'))}</button>
        </div>
        ${buildBrandHtml()}
      </div>
    `;
    document.body.appendChild(el);
    _bannerEl = el;
    trapFocus(el);
    // Focus de title (neutraal) ipv een button — anders krijgt 1 van de
    // 2 buttons de focus-ring en lijken ze niet even-prominent.
    setTimeout(() => el.querySelector('#shopbanner-title')?.focus({ preventScroll: true }), 50);

    el.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        hideBanner();
        // Compact panel = re-open na keuze; FAB moet meteen terug zodat
        // de gebruiker 'm opnieuw kan openen zonder paginarefresh.
        showFloatingButton({ pulse: false });
      }
    });
    el.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-action]');
      if (!btn) return;
      const action = btn.dataset.action;
      if (action === 'close') {
        hideBanner();
        showFloatingButton({ pulse: false });
      } else if (action === 'withdraw') {
        onWithdraw();
      } else if (action === 'update') {
        const choices = {};
        categories.forEach(c => choices[c.code] = !!c.required);
        el.querySelectorAll('input[data-code]').forEach(inp => {
          choices[inp.dataset.code] = inp.checked;
        });
        onUpdate(choices);
      }
    });
  }

  function _defaultDesc(code) {
    return ({
      necessary:       'Strikt noodzakelijke cookies om de website te laten werken (winkelwagen, login, beveiliging). Deze kunnen niet worden uitgeschakeld.',
      functional:      'Functionele cookies onthouden voorkeuren zoals taal of regio.',
      analytics:       'Statistieken over hoe de website wordt gebruikt, zonder herleidbaar te zijn tot jouw persoon.',
      marketing:       'Cookies van adverteerders om relevante advertenties te tonen op andere websites.',
      social:          'Cookies van social media-platforms voor delen en embeds.',
      personalization: 'Cookies om de inhoud aan te passen aan jouw voorkeuren en gedrag.'
    })[code] || `Categorie: ${code}`;
  }

  function hideBanner() {
    if (_bannerEl) {
      _bannerEl.remove();
      _bannerEl = null;
      try { _previousFocus?.focus(); } catch {}
    }
  }

  // ===== 8. Floating cookie-knop bottom-left (cookie-icon met GMU-logo) =====
  function showFloatingButton(opts = {}) {
    if (_floatingBtn) return;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'shopbanner-fab';
    if (opts.pulse) btn.classList.add('shopbanner-fab--pulse');
    btn.setAttribute('aria-label', T('fab_aria'));
    btn.title = T('fab_title');
    // Cookie-shape SVG (gmu-cookie-fab-full.svg) — cookie-outline + GMU-branding
    // (G-icon + GMU + tagline) gecombineerd in één bestand. De cookie zelf is
    // de visuele "cirkel" van de FAB; CSS-background is daarom transparant
    // gemaakt (zie banner.css). Shops kunnen 'm via
    // SHOP_CONTEXT.consent.logo_url_cookie_fab door een eigen SVG vervangen.
    const logoUrl = cookieFabIconUrl();
    btn.innerHTML = `<img src="${escHtml(logoUrl)}" alt="" aria-hidden="true">`;
    btn.addEventListener('click', () => window.ShopBanner.openSettings());
    document.body.appendChild(btn);
    _floatingBtn = btn;

    if (opts.pulse) {
      const stopPulse = () => btn.classList.remove('shopbanner-fab--pulse');
      btn.addEventListener('mouseenter', stopPulse, { once: true });
      setTimeout(stopPulse, 6000);
    }
  }

  function hideFloatingButton() {
    if (_floatingBtn) { _floatingBtn.remove(); _floatingBtn = null; }
  }

  // Site-eigenaar kan een eigen footer-link aanhaken:
  //   window.ShopBanner.mountTrigger('#footer-cookie-link')
  function mountTrigger(selector) {
    if (typeof selector === 'string') {
      document.querySelectorAll(selector).forEach((el) => {
        el.addEventListener('click', (e) => {
          e.preventDefault();
          window.ShopBanner.openSettings();
        });
      });
    } else if (selector instanceof Element) {
      selector.addEventListener('click', (e) => {
        e.preventDefault();
        window.ShopBanner.openSettings();
      });
    }
  }

  // ===== 9. Save consent =====
  async function saveConsent(choices, method, terms) {
    const consent_id = (crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);

    const result = await api('POST', `/klant/${cfg.klant_id}/consent`, {
      consent_id,
      terms_id:       terms.terms_id,
      terms_md5_hash: terms.md5_hash,    // expliciete link naar privacy-versie
      method,
      choices,
      source_url:     location.href,
      banner_version: cfg.banner_version || `banner-v${VERSION}`
    });

    const state = {
      consent_id,
      terms_id:        terms.terms_id,
      terms_md5_hash:  terms.md5_hash,
      choices,
      method,
      saved_at:   Date.now(),
      expires_at: Date.now() + CONSENT_TTL_MS,
      row_hash:   result.row_hash
    };
    ls.set(STATE_KEY, state);
    gtag('consent', 'update', mapToStorage(choices));
    window.dataLayer.push({ event: 'cmp_choice_made', consent_id, choices, method });
    return state;
  }

  // ===== 10. Init pipeline =====
  let _state = null, _terms = null, _categories = null;
  let _readyResolve;
  const ready = new Promise(r => { _readyResolve = r; });

  async function init() {
    // KRITISCH: opgeslagen consent MOET hersteld worden vóór elke await, zodat
    // gtag('consent','update') binnen het wait_for_update-venster (500ms) valt —
    // vóór gtm.js de GA4/Ads-tags laat vuren. Stond dit ná `await loadTerms()`
    // (externe netwerk-call), dan gingen alle hits met gcs=G1-- (denied) de deur
    // uit en zag GA4 geen observeerbare ecommerce. De localStorage-read is
    // synchroon en heeft de terms-fetch niet nodig.
    const stored = ls.get(STATE_KEY);
    const storedValid = stored && !(stored.expires_at && stored.expires_at < Date.now());
    if (storedValid) {
      gtag('consent', 'update', mapToStorage(stored.choices));
    }

    try {
      const terms = await loadTerms();
      _terms = terms;
      _categories = terms.categories;

      const expired = stored && stored.expires_at && stored.expires_at < Date.now();
      const termsChanged = stored && stored.terms_id !== terms.terms_id;

      if (stored && !expired && !termsChanged) {
        // Returning visitor - consent is al hersteld vóór loadTerms(); hier enkel state + UI.
        _state = stored;
        window.dataLayer.push({
          event: 'cmp_state_restored',
          consent_id: stored.consent_id,
          age_days: Math.floor((Date.now() - stored.saved_at) / 86400000)
        });
        showFloatingButton({ pulse: false });
        _readyResolve(_state);
      } else {
        if (expired) {
          window.dataLayer.push({ event: 'cmp_consent_expired', consent_id: stored.consent_id });
        }
        if (termsChanged) {
          window.dataLayer.push({
            event: 'cmp_terms_changed',
            old_terms_id: stored?.terms_id, new_terms_id: terms.terms_id
          });
        }
        renderBanner(terms, _categories, async (choices, method) => {
          try {
            _state = await saveConsent(choices, method, terms);
            hideBanner();
            // Eerste keer: pulse zodat user de knop opmerkt
            showFloatingButton({ pulse: true });
            _readyResolve(_state);
          } catch (e) {
            console.error('[ShopBanner] save failed', e);
            alert(T('save_error'));
          }
        });
      }
    } catch (e) {
      console.error('[ShopBanner] init failed', e);
      window.dataLayer.push({ event: 'cmp_init_failed', error: e.message });
      // Toon fallback banner zodat de bezoeker weet dat er cookies zijn,
      // ook al kunnen we geen consent registreren.
      renderFallbackBanner(classifyError(e));
      _readyResolve(null);
    }
  }

  /**
   * Vertaal de raw error naar een korte ref-code voor de fallback banner.
   * Helpt support om te diagnosticeren zonder gevoelige details te tonen.
   */
  function classifyError(e) {
    const msg = String(e && e.message || e || '');
    if (/-> 401\b/.test(msg))                 return 'auth_failed';        // verkeerde / revoked key
    if (/-> 403\b/.test(msg))                 return 'access_denied';      // cross-tenant
    if (/-> 404\b/.test(msg))                 return 'not_found';          // klant_id onbekend
    if (/No published terms/.test(msg))       return 'no_terms';           // klant heeft niets gepubliceerd
    if (/-> 5\d\d\b/.test(msg))               return 'server_error';       // API down
    if (/Failed to fetch|NetworkError/.test(msg)) return 'network_error';
    return 'unknown';
  }

  // ===== 11. Public API =====
  window.ShopBanner = {
    version: VERSION,
    ready,
    getState: () => _state,
    getTerms: () => _terms,

    openSettings: () => {
      if (!_terms) {
        console.warn('[ShopBanner] not initialized');
        return;
      }
      hideFloatingButton();
      // Re-open na keuze → compact panel bottom-left met huidige toggle-state.
      // Eerste keer (geen state) → full-width footer-bar.
      if (_state && _state.choices) {
        renderCompactPanel(
          _terms, _categories, _state.choices,
          // Withdraw: alle non-required op false
          async () => {
            try {
              await window.ShopBanner.withdraw();
              hideBanner();
              showFloatingButton({ pulse: false });
            } catch (e) {
              console.error('[ShopBanner] withdraw failed', e);
            }
          },
          // Update: nieuwe keuze opslaan
          async (choices) => {
            try {
              _state = await saveConsent(choices, 'updated', _terms);
              hideBanner();
              showFloatingButton({ pulse: false });
            } catch (e) {
              console.error('[ShopBanner] update failed', e);
            }
          }
        );
      } else {
        renderBanner(_terms, _categories, async (choices, method) => {
          try {
            _state = await saveConsent(choices, method, _terms);
            hideBanner();
            showFloatingButton({ pulse: false });
          } catch (e) {
            console.error('[ShopBanner] save failed', e);
          }
        });
      }
    },

    mountTrigger,

    withdraw: async () => {
      if (!_state || !_state.consent_id) return;
      try {
        await api('POST', `/klant/${cfg.klant_id}/consent/${_state.consent_id}/withdraw`);
        const denied = {};
        (_categories || []).forEach(c => denied[c.code] = !!c.required);
        const next = {
          ..._state,
          choices: denied,
          method: 'withdrawn',
          withdrawn_at: Date.now()
        };
        ls.set(STATE_KEY, next);
        _state = next;
        gtag('consent', 'update', mapToStorage(denied));
        window.dataLayer.push({ event: 'cmp_withdrawn', consent_id: _state.consent_id });
      } catch (e) {
        console.error('[ShopBanner] withdraw failed', e);
        throw e;
      }
    },

    reset: () => {
      ls.del(CACHE_KEY);
      ls.del(STATE_KEY);
      hideFloatingButton();
      location.reload();
    }
  };

  init();

  // CommonJS-export voor unit tests (V8-coverage werkt op file-based modules,
  // niet op `new Function(...)`-eval). Geen effect in browser-context want
  // daar is `module` undefined.
  if (typeof module !== 'undefined' && typeof module.exports === 'object') {
    module.exports = {
      T, UI_STRINGS, escHtml, normalizeLang,
      _setLang: (l) => { LANG = l; },
      _getLang: () => LANG,
    };
  }
})();
